import React, { useState, useEffect, lazy, Suspense } from 'react';
import { LogOut, LayoutDashboard, ShoppingBag, Plus, ArrowLeftRight, FileText, RefreshCw, Clock, FolderOpen, Target, Gamepad2, Calendar, CalendarDays, ClipboardList, User, Key, Star, Plane, MessageCircle, Wifi, WifiOff, Shield, CreditCard, Handshake, ScanLine, AlertTriangle, Package } from 'lucide-react';
import { Button } from './ui/button';
import DashboardTab from './DashboardTab';
import MinhasVendasTab from './MinhasVendasTab';
import TransferenciasTab from './TransferenciasTab';
import FechosTurnosTab from './FechosTurnosTab';
import FechoTurnoTab from './FechoTurnoTab';
import RevendasTab from './RevendasTab';
import StocksTab from './StocksTab';
import RegistoPontoTab from './RegistoPontoTab';
import TarefasTab from './TarefasTab';
import TarefasTurnoWrapper from './TarefasTurnoWrapper';
import RelatorioTurnoTab from './RelatorioTurnoTab';
import { useRelatorioBadge } from '../hooks/useRelatorioBadge';
import CampanhasEmVigorTab from './CampanhasEmVigorTab';
import MeusObjetivosTab from './MeusObjetivosTab';
import ValidadesTab from './ValidadesTab';
// ArtigosTab removido do perfil colaborador
import HorarioTab from './HorarioTabCompleto';
import VisualizacaoHorarioSimples from './VisualizacaoHorarioSimples';
// RegistarVendaModal removido - vendas vêm do XD
import AlertaObjetivosNovos from './AlertaObjetivosNovos';
import AlertaValidadesHoje from './AlertaValidadesHoje';
// RegistoAtividadesTab removido - fundido com Tarefas por Turno
// RegistoFaltasTab removido do perfil colaborador
import AlertaObjetivosPendentes from './AlertaObjetivosPendentes';
import ObjetivosPontuaisLembrete from './ObjetivosPontuaisLembrete';
import DocumentosTab from './DocumentosTab';
import DocumentosAdmin from './DocumentosAdmin';
import MeuPerfilContainer from './MeuPerfilContainer';
import NotificacoesDropdown from './NotificacoesDropdown';
import PedidosTrocaTab from './PedidosTrocaTab';
import ChatInterno from './ChatInterno';
import OcorrenciasTab from './OcorrenciasTab';
import { ParceriasModule } from './Parcerias';
import GestaoClientes from './Parcerias/GestaoClientes';
import AlertasLoginModal from './AlertasLoginModal';
const ConsultaOperacional = lazy(() => import('./Parcerias/ConsultaOperacional'));
const QuebrasXdTab = lazy(() => import('./QuebrasXdTab'));
const BoxJaneiraAdmin = lazy(() => import('./Box/BoxJaneiraAdmin'));
import axios from 'axios';
import useWebSocket from '../hooks/useWebSocket';

// URL centralizado que funciona em produção mesmo com .env errado
// Usar apiGet/apiPost para retry automático em erros 5xx
import { getBackendUrl, apiGet, apiPost } from '../utils/config';
const API_URL = getBackendUrl();

const ColaboradorDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { badge: rtBadge } = useRelatorioBadge();
  const [activeJogosSubTab, setActiveJogosSubTab] = useState('fechos-turnos'); // Sub-tab para Jogos
  const [showVendaModal, setShowVendaModal] = useState(false);
  const [showFechoTurnoModal, setShowFechoTurnoModal] = useState(false);
  const [acessoRestrito, setAcessoRestrito] = useState(false); // Para colaboradores fora do IP da empresa

  // Debug: Monitor estado do modal
  useEffect(() => {
    console.log('🔍 Estado showFechoTurnoModal mudou para:', showFechoTurnoModal);
  }, [showFechoTurnoModal]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [objetivosNovos, setObjetivosNovos] = useState([]);
  const [showAlertaObjetivos, setShowAlertaObjetivos] = useState(false);
  const [meuHorario, setMeuHorario] = useState(null);
  const [loadingHorario, setLoadingHorario] = useState(false);
  const [visualizarTodos, setVisualizarTodos] = useState(false);
  const [showObjetivosPontuaisModal, setShowObjetivosPontuaisModal] = useState(false);
  const [documentosPendentes, setDocumentosPendentes] = useState(0);
  const [horariosNaoVistos, setHorariosNaoVistos] = useState(0);
  const [trocasPendentes, setTrocasPendentes] = useState(0);
  const [mensagensNaoLidas, setMensagensNaoLidas] = useState(0);
  const [perfilIncompleto, setPerfilIncompleto] = useState(false);
  const [scrollChatToUnread, setScrollChatToUnread] = useState(false);
  const [chatTargetInfo, setChatTargetInfo] = useState(null); // Info para abrir conversa específica
  const [parceiraSubTab, setParceiraSubTab] = useState(null);
  const [ipClienteDetectado, setIpClienteDetectado] = useState('');

  // Verificar acesso por IP (colaboradores fora da empresa só vêem horários)
  const verificarAcessoIP = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verificar-acesso-ip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAcessoRestrito(!data.acesso_total);
        // 🛡️ 12-02-2026 (sessão 26) — Expor IP do cliente no banner para
        // o utilizador poder partilhar com o admin se a loja mudou de IP.
        setIpClienteDetectado(data.ip_cliente || '');
        
        // Se acesso restrito, redirecionar para horários
        if (!data.acesso_total && activeTab !== 'horario') {
          setActiveTab('horario');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar acesso IP:', error);
      // Em caso de erro, não restringir acesso
      setAcessoRestrito(false);
    }
  };

  // Função para carregar TODAS as notificações/contadores
  const carregarTodasNotificacoes = async () => {
    carregarDocumentosPendentes();
    carregarHorariosNaoVistos();
    carregarTrocasPendentes();
    carregarMensagensNaoLidas();
    carregarStatusPerfil();
  };

  // Carregar status do perfil (se está completo ou não)
  const carregarStatusPerfil = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/menu-colaborador/perfil/meu`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const percentagem = response.data?.percentagem_completo || 0;
      setPerfilIncompleto(percentagem < 100);
    } catch (error) {
      console.error('Erro ao carregar status do perfil:', error);
    }
  };

  useEffect(() => {
    // Verificar acesso por IP primeiro
    verificarAcessoIP();
    // Verificar objetivos novos ao carregar o dashboard
    checkObjetivosNovos();
    // Carregar todas as notificações inicialmente
    carregarTodasNotificacoes();
    
    // POLLING: Atualizar notificações a cada 30 segundos como fallback
    // (WebSocket atualiza em tempo real quando disponível)
    const interval = setInterval(carregarTodasNotificacoes, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  // 🔔 WEBSOCKET: Notificações em tempo real
  const { isConnected: wsConnected } = useWebSocket({
    onChatMessage: () => {
      // Nova mensagem no chat - atualizar contador
      carregarMensagensNaoLidas();
    },
    onDocument: () => {
      // Novo documento para mim
      carregarTodasNotificacoes();
    },
    onHorario: () => {
      // Novo horário publicado
      carregarTodasNotificacoes();
    },
    onTarefa: () => {
      // Nova tarefa atribuída
      carregarTodasNotificacoes();
    },
    onObjetivo: (data) => {
      // Objetivo atingido!
      console.log('🎉 Objetivo atingido:', data);
      carregarTodasNotificacoes();
      checkObjetivosNovos();
    },
    onUpdateCounters: () => {
      // Pedido para atualizar contadores
      carregarTodasNotificacoes();
    }
  });

  const carregarMensagensNaoLidas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/nao-lidas/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMensagensNaoLidas(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao contar mensagens não lidas:', error);
    }
  };

  const checkObjetivosNovos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/objetivos-pontuais/meus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.novos && response.data.novos.length > 0) {
        setObjetivosNovos(response.data.novos);
        setShowAlertaObjetivos(true);
      }
    } catch (error) {
      console.error('Erro ao verificar objetivos novos:', error);
    }
  };

  const handleMarcarVisualizado = async (objetivoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/objetivos-pontuais/${objetivoId}/marcar-visualizado`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erro ao marcar como visualizado:', error);
    }
  };

  const handleVendaSuccess = async () => {
    setRefreshKey(prev => prev + 1);
    
    // Verificar se há objetivos pontuais ativos
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/objetivos-pontuais/meus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const objetivosAtivos = response.data.ativos || [];
      if (objetivosAtivos.length > 0) {
        // Mostrar modal de objetivos pontuais
        setShowObjetivosPontuaisModal(true);
      }
    } catch (error) {
      console.error('Erro ao verificar objetivos:', error);
    }
  };

  // Carregar contagem de documentos pendentes
  const carregarDocumentosPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documentos/count/pendentes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocumentosPendentes(data.count || 0);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos pendentes:', error);
    }
  };

  // Carregar contagem de horários não vistos
  const carregarHorariosNaoVistos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/horarios-mensais/count/nao-vistos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setHorariosNaoVistos(response.data.count || 0);
    } catch (error) {
      console.error('Erro ao carregar horários não vistos:', error);
    }
  };

  // Carregar contagem de pedidos de troca pendentes
  const carregarTrocasPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/menu-colaborador/pedidos-troca/count/pendentes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setTrocasPendentes(response.data.count || 0);
    } catch (error) {
      console.error('Erro ao carregar trocas pendentes:', error);
    }
  };


  // Carregar horário do colaborador logado
  useEffect(() => {
    if (activeTab === 'meu-horario') {
      carregarMeuHorario();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const carregarMeuHorario = async () => {
    setLoadingHorario(true);
    try {
      const token = localStorage.getItem('token');
      // Buscar horários mensais
      const response = await axios.get(`${API_URL}/api/horarios-mensais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar apenas os horários que contenham o colaborador logado
      // Usar String() para garantir comparação correcta entre diferentes tipos
      const horariosDoColaborador = response.data.filter(h => 
        h.colaboradores_horarios.some(c => String(c.colaborador_id) === String(user.id))
      );

      if (horariosDoColaborador.length > 0) {
        // Pegar o horário mais recente (primeiro do array, já vem ordenado do backend)
        const horarioMaisRecente = horariosDoColaborador[0];
        
        // Marcar horário como visto
        try {
          await axios.post(
            `${API_URL}/api/horarios-mensais/${horarioMaisRecente.id}/marcar-visto`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Atualizar contagem após marcar como visto
          carregarHorariosNaoVistos();
        } catch (error) {
          console.error('Erro ao marcar horário como visto:', error);
        }
        
        // Filtrar apenas os dados do colaborador logado
        const horarioFiltrado = {
          ...horarioMaisRecente,
          colaboradores_horarios: horarioMaisRecente.colaboradores_horarios.filter(
            c => String(c.colaborador_id) === String(user.id)
          )
        };
        setMeuHorario(horarioFiltrado);
      } else {
        setMeuHorario(null);
      }
    } catch (error) {
      console.error('Erro ao carregar horário:', error);
      setMeuHorario(null);
    } finally {
      setLoadingHorario(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-shrink">
              <h1 className="text-lg sm:text-2xl font-display font-bold truncate">Dashboard</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Bem-vindo, {user.nome}</p>
            </div>
            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
              {!acessoRestrito && (
              <>
              <Button 
                onClick={() => setActiveTab('operacao')} 
                variant="default"
                className="shadow-elegant hidden sm:flex bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300"
                size="sm"
                data-testid="btn-operacao-header"
              >
                <ScanLine className="h-4 w-4 sm:mr-2" />
                <span className="hidden md:inline">Operação</span>
              </Button>
              <Button 
                onClick={() => setActiveTab('operacao')} 
                variant="default"
                className="shadow-elegant sm:hidden p-2 bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300"
                size="sm"
                title="Operação"
                data-testid="btn-operacao-header-mobile"
              >
                <ScanLine className="h-4 w-4" />
              </Button>
              <Button 
                onClick={() => {
                  console.log('🎮 Registo de Jogos clicado!');
                  setShowFechoTurnoModal(true);
                  console.log('Estado showFechoTurnoModal =', true);
                }} 
                variant="default"
                className="shadow-elegant hidden sm:flex"
                size="sm"
              >
                <Gamepad2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden md:inline">Registo de Jogos</span>
              </Button>
              <Button 
                onClick={() => setShowFechoTurnoModal(true)} 
                variant="default"
                className="shadow-elegant sm:hidden p-2"
                size="sm"
                title="Registo de Jogos"
              >
                <Gamepad2 className="h-4 w-4" />
              </Button>
              </>
              )}
              <NotificacoesDropdown 
                onNavigateToChat={(chatInfo) => {
                  setActiveTab('chat');
                  setScrollChatToUnread(true);
                  setChatTargetInfo(chatInfo || null);
                  carregarMensagensNaoLidas();
                }}
                onNavigateToTab={(tab, extraInfo) => {
                  // Mapear tabs do admin para tabs do colaborador se necessário
                  const tabMapping = {
                    'gestao-colaboradores': 'ferias',
                    'tarefas-turno': 'tarefas',
                    'horarios': 'horarios'
                  };
                  const targetTab = tabMapping[tab] || tab;
                  setActiveTab(targetTab);
                }}
              />
              <Button variant="outline" onClick={onLogout} size="sm" className="p-2 sm:px-3">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
          
          {/* Aviso de acesso restrito */}
          {acessoRestrito && (
            <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded-lg flex flex-col sm:flex-row sm:items-center gap-2 text-yellow-800 text-sm">
              <Shield className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1">
                <strong>Acesso Remoto:</strong> Está fora da rede da empresa. Apenas Horário e Chat estão disponíveis.
                {ipClienteDetectado && (
                  <div className="mt-1 text-xs">
                    Seu IP detectado: <span className="font-mono bg-yellow-200 px-1 py-0.5 rounded select-all" data-testid="ip-cliente-detectado">{ipClienteDetectado}</span>
                    <span className="ml-1 text-yellow-700">— partilhe com o administrador para autorizar.</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-1 sm:gap-2 mt-2 sm:mt-4 md:pb-0 overflow-x-auto md:overflow-x-visible pb-2 -mx-2 px-2 md:mx-0 md:px-0 scrollbar-thin md:scrollbar-none">
            {/* 1º Dashboard */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'dashboard' ? 'default' : 'outline'}
              onClick={() => setActiveTab('dashboard')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <LayoutDashboard className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Dashboard
            </Button>
            )}
            
            {/* 2º Meus Objetivos */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'objetivos' ? 'default' : 'outline'}
              onClick={() => setActiveTab('objetivos')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <Target className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Meus Objetivos
            </Button>
            )}
            
            {/* 3º Jogos */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'jogos' ? 'default' : 'outline'}
              onClick={() => setActiveTab('jogos')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <Gamepad2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Jogos
            </Button>
            )}
            
            {/* 4º Validades */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'validades' ? 'default' : 'outline'}
              onClick={() => setActiveTab('validades')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Validades
            </Button>
            )}

            {/* 4.5º Quebras */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'quebras' ? 'default' : 'outline'}
              onClick={() => setActiveTab('quebras')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              data-testid="tab-quebras"
            >
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Quebras
            </Button>
            )}
            
            {/* 5º Documentos */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'documentos' ? 'default' : 'outline'}
              onClick={() => {
                setActiveTab('documentos');
                carregarDocumentosPendentes();
              }}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative"
            >
              <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Documentos
              {documentosPendentes > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {documentosPendentes}
                </span>
              )}
            </Button>
            )}
            
            {/* 5.5º Relatório de Turno (iter289 — substitui Tarefas por Turno) */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'tarefas-turno' ? 'default' : 'outline'}
              onClick={() => setActiveTab('tarefas-turno')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative"
              data-testid="menu-relatorio-turno"
            >
              <ClipboardList className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Relatório de Turno
              {rtBadge.count > 0 && (
                <span
                  className={`ml-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 ${
                    rtBadge.urgentes_por_ler > 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
                  }`}
                  data-testid="rt-menu-badge"
                  title={`${rtBadge.urgentes_por_ler} urgentes por ler · ${rtBadge.pendentes_arrastados} pendentes`}
                >
                  {rtBadge.count > 99 ? '99+' : rtBadge.count}
                </span>
              )}
            </Button>
            )}

            {/* 5.6º Campanhas em Vigor */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'campanhas-vigor' ? 'default' : 'outline'}
              onClick={() => setActiveTab('campanhas-vigor')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              data-testid="btn-tab-campanhas-vigor"
            >
              <Star className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Campanhas em Vigor
            </Button>
            )}
            
            {/* 6º Registo de Ponto */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'ponto' ? 'default' : 'outline'}
              onClick={() => setActiveTab('ponto')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Registo Ponto
            </Button>
            )}
            
            {/* 7.5º Minhas Vendas */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'minhas-vendas' ? 'default' : 'outline'}
              onClick={() => setActiveTab('minhas-vendas')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Minhas Vendas
            </Button>
            )}
            
            {/* 9º Meu Horário - SEMPRE VISÍVEL */}
            <Button
              variant={activeTab === 'meu-horario' ? 'default' : 'outline'}
              onClick={() => setActiveTab('meu-horario')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative"
            >
              <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Meu Horário
              {horariosNaoVistos > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {horariosNaoVistos}
                </span>
              )}
            </Button>
            
            {/* 10º Chat Interno (também disponível em acesso remoto) */}
            <Button
              variant={activeTab === 'chat' ? 'default' : 'outline'}
              onClick={() => {
                setActiveTab('chat');
                carregarMensagensNaoLidas();
                setScrollChatToUnread(true); // Ativar scroll para mensagens não lidas
              }}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative"
            >
              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Chat
              {mensagensNaoLidas > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {mensagensNaoLidas}
                </span>
              )}
            </Button>
            
            
            {/* Clientes Janeira */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'clientes-janeira' ? 'default' : 'outline'}
              onClick={() => setActiveTab('clientes-janeira')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              data-testid="tab-clientes-janeira"
            >
              <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Clientes
            </Button>
            )}
            
            {/* Parcerias */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'parcerias' ? 'default' : 'outline'}
              onClick={() => { setActiveTab('parcerias'); setParceiraSubTab(null); }}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <Handshake className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Parcerias
            </Button>
            )}
            
            {/* 11º Box Janeira (reservas anti-desperdicio) - todos os colaboradores */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'box' ? 'default' : 'outline'}
              onClick={() => setActiveTab('box')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              data-testid="tab-box"
              title="Box Janeira - Reservas anti-desperdicio"
            >
              <Package className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Box
            </Button>
            )}

            {/* 11º Meu Perfil (com sub-abas incluindo Trocas) */}
            {!acessoRestrito && (
            <Button
              variant={activeTab === 'meu-perfil' ? 'default' : 'outline'}
              onClick={() => setActiveTab('meu-perfil')}
              className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative"
            >
              <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Meu Perfil
              {(trocasPendentes > 0 || perfilIncompleto) && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {trocasPendentes > 0 ? trocasPendentes : '!'}
                </span>
              )}
            </Button>
            )}
          </div>
        </div>
      </header>
      
      {/* Sub-navegação para Jogos */}
      {activeTab === 'jogos' && (
        <div className="bg-card/30 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex space-x-2 py-3 overflow-x-auto">
              <Button
                variant={activeJogosSubTab === 'fechos-turnos' ? 'default' : 'ghost'}
                onClick={() => setActiveJogosSubTab('fechos-turnos')}
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              >
                <ClipboardList className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Fecho de Jogos
              </Button>
              <Button
                variant={activeJogosSubTab === 'stocks' ? 'default' : 'ghost'}
                onClick={() => setActiveJogosSubTab('stocks')}
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              >
                <FolderOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Stocks
              </Button>
              <Button
                variant={activeJogosSubTab === 'revendas' ? 'default' : 'ghost'}
                onClick={() => setActiveJogosSubTab('revendas')}
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              >
                <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Revenda Raspadinhas
              </Button>
              <Button
                variant={activeJogosSubTab === 'transferencias' ? 'default' : 'ghost'}
                onClick={() => setActiveJogosSubTab('transferencias')}
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
              >
                <ArrowLeftRight className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Transferências
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <main key={refreshKey} className="container mx-auto px-4 py-8">
        {/* Alerta de Pedidos de Troca Pendentes */}
        {trocasPendentes > 0 && (
          <div 
            className="mb-4 p-4 bg-gradient-to-r from-orange-100 to-yellow-100 border-l-4 border-orange-500 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('meu-perfil')}
          >
            <div className="flex items-center gap-3">
              <div className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg animate-pulse">
                {trocasPendentes}
              </div>
              <div>
                <p className="font-bold text-orange-800">
                  🔔 Tem {trocasPendentes} pedido{trocasPendentes > 1 ? 's' : ''} de troca para responder!
                </p>
                <p className="text-sm text-orange-600">
                  Um colega pediu para trocar de turno consigo. Clique aqui para ver e responder.
                </p>
              </div>
              <ArrowLeftRight className="h-6 w-6 text-orange-500 ml-auto" />
            </div>
          </div>
        )}
        
        {/* Alerta de Objetivos Pendentes - Aparece em todas as abas */}
        <AlertaObjetivosPendentes onNavigate={setActiveTab} />
        
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'vendas' && <MinhasVendasTab />}
        {activeTab === 'minhas-vendas' && <MinhasVendasTab />}
        {activeTab === 'objetivos' && <MeusObjetivosTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'fechos-turnos' && <FechosTurnosTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'stocks' && <StocksTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'revendas' && <RevendasTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'transferencias' && <TransferenciasTab />}
        {activeTab === 'ponto' && <RegistoPontoTab />}
        {activeTab === 'tarefas' && <TarefasTab />}
        {/* Documentos: Responsável usa DocumentosAdmin (criar/editar), Colaborador usa DocumentosTab (visualizar) */}
        {activeTab === 'documentos' && user.role === 'responsavel' && <DocumentosAdmin />}
        {activeTab === 'documentos' && user.role !== 'responsavel' && <DocumentosTab user={user} />}
        {activeTab === 'tarefas-turno' && <RelatorioTurnoTab user={user} />}
        {activeTab === 'campanhas-vigor' && <CampanhasEmVigorTab />}
        {activeTab === 'validades' && <ValidadesTab />}
        {activeTab === 'quebras' && (
          <Suspense fallback={<div className="text-center py-12">Carregando...</div>}>
            <QuebrasXdTab user={user} />
          </Suspense>
        )}
        {activeTab === 'box' && (
          <Suspense fallback={<div className="text-center py-12">Carregando...</div>}>
            <BoxJaneiraAdmin user={user} />
          </Suspense>
        )}
        {activeTab === 'meu-perfil' && <MeuPerfilContainer trocasPendentes={trocasPendentes} onAtualizarTrocas={carregarTrocasPendentes} />}
        {activeTab === 'chat' && (
          <ChatInterno 
            user={user} 
            scrollToUnread={scrollChatToUnread}
            targetInfo={chatTargetInfo}
            onTargetHandled={() => setChatTargetInfo(null)}
          />
        )}
        {activeTab === 'operacao' && (
          <Suspense fallback={<div className="text-center py-12">Carregando...</div>}>
            <ConsultaOperacional user={user} />
          </Suspense>
        )}
        {activeTab === 'parcerias' && <ParceriasModule user={user} defaultTab={parceiraSubTab} />}
        {activeTab === 'clientes-janeira' && <GestaoClientes user={user} />}
        {activeTab === 'meu-horario' && (
          <div className="p-4">
            {loadingHorario ? (
              <div className="text-center p-8">
                <p className="text-gray-500">A carregar horário...</p>
              </div>
            ) : meuHorario ? (
              <>
                {/* Toggle para ver meu horário ou todos */}
                <div className="mb-4 flex gap-2">
                  <button
                    onClick={() => setVisualizarTodos(false)}
                    className={`px-4 py-2 rounded-lg font-semibold ${
                      !visualizarTodos 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Meu Horário
                  </button>
                  <button
                    onClick={() => setVisualizarTodos(true)}
                    className={`px-4 py-2 rounded-lg font-semibold ${
                      visualizarTodos 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Todos os Colaboradores
                  </button>
                </div>

                {visualizarTodos ? (
                  /* Mostrar tabela completa igual ao admin, mas em modo readOnly */
                  <HorarioTab readOnly={true} />
                ) : (
                  /* Mostrar visualização horizontal apenas do colaborador */
                  <VisualizacaoHorarioSimples 
                    horario={meuHorario}
                    colaboradores={[]}
                    colaboradorSelecionadoId={user.id}
                    modoColaborador={true}
                    visualizarTodos={false}
                  />
                )}
              </>
            ) : (
              <div className="text-center p-8">
                <p className="text-gray-500">Nenhum horário disponível</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Componente FechoTurnoTab - quando autoOpenModal=true, renderiza apenas o modal */}
      {showFechoTurnoModal && (
        <FechoTurnoTab 
          autoOpenModal={true}
          onModalClose={() => setShowFechoTurnoModal(false)}
        />
      )}

      {/* Alerta de Objetivos Novos */}
      {showAlertaObjetivos && (
        <AlertaObjetivosNovos
          objetivos={objetivosNovos}
          onClose={() => setShowAlertaObjetivos(false)}
          onMarcarVisualizado={handleMarcarVisualizado}
        />
      )}

      {/* Alerta de Validades que Expiram Hoje */}
      <AlertaValidadesHoje />
      
      {/* Lembrete de Objetivos Pontuais após Registar Venda */}
      <ObjetivosPontuaisLembrete
        isOpen={showObjetivosPontuaisModal}
        onClose={() => setShowObjetivosPontuaisModal(false)}
        onGoToObjetivos={() => {
          setShowObjetivosPontuaisModal(false);
          setActiveTab('objetivos');
        }}
      />
      
      {/* Modal Alertas Login */}
      <AlertasLoginModal />
    </div>
  );
};

export default ColaboradorDashboard;
